/**
 * Who a person is talking to.
 *
 * Namu is the company and the product. Nadia and Salim are the personas, and
 * each one names itself when it speaks: the backend holds the prompt that puts
 * "Ni ce Nadia" in one mouth and "Ni ne Salim" in the other. That is why
 * nothing in `src/i18n` or `src/onboarding/script.ts` writes a persona's name
 * into a sentence. Copy that says a name is a second claim about who is
 * talking, made by the half of the system that cannot hear the answer, and the
 * two come apart the moment an upstream default moves.
 *
 * What lives here is the little the device genuinely has to know: which
 * personas can be offered, what to label them in the picker, and the line each
 * one opens with so that somebody choosing can see what they are choosing.
 * Adding a third costs one entry below and nothing else.
 */

export const PERSONA_IDS = ['nadia', 'salim'] as const

export type PersonaId = (typeof PERSONA_IDS)[number]

export interface Persona {
  id: PersonaId
  /**
   * The picker's label, and the only place in the app a persona name is
   * written down. A label names a choice; it does not put words in a mouth.
   */
  name: string
  /**
   * How this one opens, in Hausa.
   *
   * Copied from the personas the backend publishes on its health endpoint, so
   * the picker shows the actual sentence rather than a description of it. The
   * copula carries the difference the English loses: `ce` for Nadia and `ne`
   * for Salim. It is not translated into English for the same reason the
   * voices are not: this is a sample of what will be spoken, and what will be
   * spoken is Hausa.
   */
  greeting: string
}

export const PERSONAS: Record<PersonaId, Persona> = {
  nadia: {
    id: 'nadia',
    name: 'Nadia',
    greeting: 'Sannu. Ni ce Nadia. Me zan taimaka maka da shi yau?',
  },
  salim: {
    id: 'salim',
    name: 'Salim',
    greeting: 'Sannu. Ni ne Salim. Menene tambayarka?',
  },
}

/**
 * The one offered to somebody who has never chosen.
 *
 * Matches the backend's own default. Naming it on every request anyway is the
 * server's job and the server's reasoning: two services default
 * independently, and an agent that has moved while the synthesiser has not
 * introduces itself as a man in a woman's voice.
 */
export const DEFAULT_PERSONA: PersonaId = 'nadia'

export function isPersonaId(value: unknown): value is PersonaId {
  return typeof value === 'string' && (PERSONA_IDS as readonly string[]).includes(value)
}

/** For anything read back off a device, where the stored value is untrusted. */
export function personaOrDefault(value: unknown): PersonaId {
  return isPersonaId(value) ? value : DEFAULT_PERSONA
}

export function personaList(): readonly Persona[] {
  return PERSONA_IDS.map((id) => PERSONAS[id])
}
